import { CallState } from "@b2bportal/core-types";
import {
  type Lodging,
  type AmenityStatus,
  LodgingSelectionEnum,
  Platform,
  LocationDescriptorEnum,
  type LodgingHomesAmenity,
  StayType,
  LodgingCollection,
} from "@b2bportal/lodging-api/lib/api";
import {
  ensureExhaustive,
  getDeviceType,
  hotelListSortComparatorsMap,
  toHotelCoordinatesType,
} from "@overrides/utilities";
import { createSelector } from "@reduxjs/toolkit";
import {
  performAmenitiesFilter,
  performCancellationFilter,
  performHomeAmenitiesFilter,
  performHomesRoomsAndBedsFilter,
  performHotelNameFilter,
  performMealPlanFilter,
  performPayLaterFilter,
  performPriceFilter,
  performStarRatingFilter,
  performStayTypeFilter,
  performUserRatingFilter,
} from "@tenant/hotels";
import dayjs from "dayjs";
import type { HotelsState } from "../../../store";

import { initialFiltersState } from "./initialState";

export const getSearchFormGuests = (state: HotelsState) =>
  state.hotels.hotelsAvailability.searchFormValues.guests;

export const getSearchFormPlace = (state: HotelsState) =>
  state.hotels.hotelsAvailability.searchFormValues.place;

export const getSearchFormDateRange = (state: HotelsState) =>
  state.hotels.hotelsAvailability.searchFormValues.dateRange;

export const getSearchFormRoomsCount = (state: HotelsState) =>
  state.hotels.hotelsAvailability.searchFormValues.rooms;

export const getSearch = (state: HotelsState) =>
  state.hotels.hotelsAvailability.search;

export const getRequestedSearch = (state: HotelsState) =>
  state.hotels.hotelsAvailability.requestedSearch;

export const getOffersById = (state: HotelsState) =>
  state.hotels.hotelsAvailability.hotels.data?.offersByProductId ?? {};

export const getTotalNights = (state: HotelsState) => {
  const search = getSearch(state);

  if (search == null) return undefined;

  const { dateRange } = search;
  if (dateRange.from != null && dateRange.until != null) {
    const fromDate = dayjs(dateRange.from);
    const untilDate = dayjs(dateRange.until);
    return untilDate.diff(fromDate, "days");
  }
};
export const getDestination = (state: HotelsState) =>
  state.hotels.hotelsAvailability.search?.place;

export const getUnsortedHotels = createSelector(
  (state: HotelsState) => state.hotels.hotelsAvailability.hotels.data?.items,
  (items) => items ?? []
);

export const getTotalPropertyCount = (state: HotelsState) =>
  state.hotels.hotelsAvailability.hotels.data?.totalPropertyCount;

export const getAvailabilityTrackingProperties = (state: HotelsState) =>
  state.hotels.hotelsAvailability.hotels.data?.trackingProperties ?? [];

export const getHotelsEqualityCheck = (
  prevHotels: Lodging[],
  newHotels: Lodging[]
) => {
  return (
    prevHotels.length === newHotels.length &&
    // This should only execute if the length of the arrays is the same
    prevHotels.every(
      (hotel, index) => hotel.lodging.id === newHotels[index].lodging.id
    )
  );
};

export const getNextPageToken = (state: HotelsState) =>
  state.hotels.hotelsAvailability.hotels.data?.nextPageToken;

export const isLoading = (state: HotelsState) => {
  const { state: callState, data } = state.hotels.hotelsAvailability.hotels;
  return (
    callState === CallState.InProcess && (!data || data?.items.length === 0)
  );
};

export const getSearchId = (state: HotelsState) =>
  state.hotels.hotelsAvailability.hotels.data?.searchId;

export const isLoadingNext = (state: HotelsState) => {
  const { state: callState, data } = state.hotels.hotelsAvailability.hotels;
  return callState === CallState.InProcess && data?.nextPageToken != null;
};

export const isInitializingValues = (state: HotelsState) =>
  state.hotels.hotelsAvailability.initialized === CallState.InProcess;

export const hasInitializeError = (state: HotelsState) =>
  state.hotels.hotelsAvailability.initialized === CallState.Failed;

export const hasError = (state: HotelsState) => {
  const { state: callState, data } = state.hotels.hotelsAvailability.hotels;
  // Only surface the error UI when there are no hotels to show.
  // If a follow-up (pagination) fetch fails we already have a partial list —
  // keep it visible and silently stop fetching more.
  return callState === CallState.Failed && (data?.items?.length ?? 0) === 0;
};

export const hasNeverFetchedHotels = (state: HotelsState) =>
  state.hotels.hotelsAvailability.hotels.state === CallState.NotCalled;

export const isAvailabilityPageLoading = (state: HotelsState) =>
  isLoading(state) || hasNeverFetchedHotels(state);

export const getSort = (state: HotelsState) =>
  state.hotels.hotelsAvailability.sort;

export const getHotels = createSelector(
  getUnsortedHotels,
  getSort,
  (rawHotels, sort) => {
    const sortComparator = hotelListSortComparatorsMap[sort];
    // Always return a new array (never return the input directly) to satisfy
    // reselect's identity-function check and avoid stale reference issues.
    const sorted = rawHotels.slice();
    if (sortComparator != null) {
      sorted.sort(sortComparator);
    }
    return sorted;
  }
);

export const getFilters = (state: HotelsState) =>
  state.hotels.hotelsAvailability.filters;

export const getPriceFilter = (state: HotelsState) =>
  state.hotels.hotelsAvailability.filters.priceRange;

export const getStarRatingFilter = (state: HotelsState) =>
  state.hotels.hotelsAvailability.filters.starRating;

export const getCancellationFilter = (state: HotelsState) =>
  state.hotels.hotelsAvailability.filters.freeCancellation;

export const getPayLaterFilter = (state: HotelsState) =>
  state.hotels.hotelsAvailability.filters.payLater;

export const getAmenitiesFilter = (state: HotelsState) =>
  state.hotels.hotelsAvailability.filters.amenities;

export const getHotelNameFilter = (state: HotelsState) =>
  state.hotels.hotelsAvailability.filters.hotelName;

export const getMealPlanFilter = (state: HotelsState) =>
  state.hotels.hotelsAvailability.filters.mealPlan;

export const getStayTypeFilter = (state: HotelsState) =>
  state.hotels.hotelsAvailability.filters.stayType;

export const getHomeAmenitiesFilter = (state: HotelsState) =>
  state.hotels.hotelsAvailability.filters.homeAmenities;

export const getHomeRoomsAndBedsFilter = (state: HotelsState) =>
  state.hotels.hotelsAvailability.filters.homeRoomsAndBeds;

export const getUserRatingFilter = (state: HotelsState) =>
  state.hotels.hotelsAvailability.filters.userRating ?? 0;

export const getPriceFilterOptions = (state: HotelsState) => {
  const hotels = getHotels(state);
  const hotelsPrice = hotels.flatMap((hotel) => {
    const { price } = hotel;
    return price == null
      ? []
      : [price.nightlyDiscountAware.priceWithUnmanagedDiscounts.fiat];
  });

  const initialPriceOption = hotelsPrice.pop();

  if (initialPriceOption == null) {
    return;
  }

  const priceFilterOptions = hotelsPrice.reduce(
    (carry, item) => {
      return {
        ...carry,
        min: {
          ...carry.min,
          value: Math.min(item.value, carry.min.value),
        },
        max: {
          ...carry.max,
          value: Math.max(item.value, carry.max.value),
        },
      };
    },
    {
      min: initialPriceOption,
      max: initialPriceOption,
    }
  );

  return priceFilterOptions;
};
export const getAmenitiesFilterOptions = (
  state: HotelsState
): AmenityStatus[] => {
  const hotels = getHotels(state);

  const amenities = hotels
    .flatMap((hotel) => hotel.lodging.amenities)
    .reduce((carry, item) => {
      //remove duplicates when the amenity is the same
      return carry[item.amenity] == null
        ? {
            ...carry,
            [item.amenity]: item,
          }
        : carry;
    }, {});

  return Array.from(Object.values(amenities));
};
export const getHomeAmenitiesFilterOptions = (
  state: HotelsState
): LodgingHomesAmenity[] => {
  const hotels = getHotels(state);

  const allAmenities = hotels.flatMap(
    (hotel) => hotel.homesListingDetails?.listingContent.amenities ?? []
  );
  return [...new Set(allAmenities)];
};

export const getFilterOptions = createSelector(
  getPriceFilterOptions,
  getAmenitiesFilterOptions,
  getHomeAmenitiesFilterOptions,
  (priceRange, amenities, homeAmenities) => {
    return {
      priceRange,
      amenities,
      homeAmenities,
    };
  }
);

export const hasSetPriceFilterSelector = createSelector(
  getPriceFilter,
  getFilterOptions,
  (priceFilter, initialFilterOptions) => {
    return (
      (priceFilter?.max !== initialFilterOptions.priceRange?.max.value &&
        priceFilter?.max !== initialFiltersState.priceRange.max) ||
      (priceFilter?.min !== initialFilterOptions.priceRange?.min.value &&
        priceFilter.min !== initialFiltersState.priceRange.min)
    );
  }
);

export const hasSetRatingFilter = createSelector(
  getStarRatingFilter,
  (starRatingFilter) => {
    return starRatingFilter.length > 0;
  }
);
export const hasSetCancellationFilter = createSelector(
  getCancellationFilter,
  (cancellationFilter) => {
    return cancellationFilter;
  }
);

export const hasSetPayLaterFilter = getPayLaterFilter;

export const hasSetAmenitiesFilter = createSelector(
  getAmenitiesFilter,
  (amenitiesFilter) => {
    return amenitiesFilter.length > 0;
  }
);

export const hasSetMealPlanFilter = createSelector(
  getMealPlanFilter,
  (mealPlanFilter) => {
    return mealPlanFilter.length > 0;
  }
);

export const hasSetUserRatingFilter = createSelector(
  getUserRatingFilter,
  (userRatingFilter) => {
    return userRatingFilter !== 0;
  }
);

export const amountOfSelectedFilters = createSelector(
  hasSetPriceFilterSelector,
  hasSetRatingFilter,
  hasSetAmenitiesFilter,
  hasSetCancellationFilter,
  hasSetPayLaterFilter,
  hasSetMealPlanFilter,
  hasSetUserRatingFilter,
  (...args): number => {
    return args.filter((arg: boolean) => arg).length;
  }
);

export const hasSetFiltersSelector = createSelector(
  amountOfSelectedFilters,
  (amount): boolean => {
    return amount > 0;
  }
);

/**
 * @deprecated
 * This blocks the thread causing the UI to lag on large list.
 * use hotelList from useAvailabilityHook which uses useTransition to get around this issue.
 * CAN STILL BE USED FOR OTHER OPERATIONS, such as Tracking
 */
export const getFilteredHotels = createSelector(
  getHotels,
  getStarRatingFilter,
  getPriceFilter,
  getCancellationFilter,
  getAmenitiesFilter,
  hasSetPriceFilterSelector,
  getPayLaterFilter,
  getHotelNameFilter,
  getMealPlanFilter,
  getStayTypeFilter,
  getHomeRoomsAndBedsFilter,
  getHomeAmenitiesFilter,
  getUserRatingFilter,
  (
    hotels,
    starRatingFilter,
    priceFilter,
    cancellationFilter,
    amenitiesFilter,
    hasSetPriceFilter,
    payLaterFilter,
    hotelNameFilter,
    mealPlanFilter,
    stayTypeFilter,
    homeRoomsAndBedsFilter,
    homeAmenitiesFilter,
    userRatingFilter
  ) => {
    const filteredHotels = hotels.filter((hotel): boolean => {
      return (
        performStarRatingFilter(hotel, starRatingFilter) &&
        performPriceFilter(hotel, hasSetPriceFilter, priceFilter) &&
        performCancellationFilter(hotel, cancellationFilter) &&
        performAmenitiesFilter(hotel, amenitiesFilter, stayTypeFilter) &&
        performPayLaterFilter(hotel, payLaterFilter) &&
        performHotelNameFilter(hotel, hotelNameFilter) &&
        performMealPlanFilter(hotel, mealPlanFilter) &&
        performStayTypeFilter(hotel, stayTypeFilter) &&
        performHomesRoomsAndBedsFilter(
          hotel,
          homeRoomsAndBedsFilter,
          stayTypeFilter
        ) &&
        performHomeAmenitiesFilter(
          hotel,
          homeAmenitiesFilter,
          stayTypeFilter
        ) &&
        performUserRatingFilter(hotel, userRatingFilter)
      );
    });
    return filteredHotels;
  }
);

export const getCentroid = (state: HotelsState) =>
  state.hotels.hotelsAvailability.map.centroid;

export const getMapZoom = (state: HotelsState) =>
  state.hotels.hotelsAvailability.map.zoom;

export const getMapBoundingBox = (state: HotelsState) =>
  state.hotels.hotelsAvailability.map.boundingBox;

export const getSearchBoundingBox = (state: HotelsState) =>
  state.hotels.hotelsAvailability.map.searchBoundingBox;

export const getAllFilters = createSelector(
  [
    getStarRatingFilter,
    getPriceFilter,
    getCancellationFilter,
    getAmenitiesFilter,
    hasSetPriceFilterSelector,
    getPayLaterFilter,
    getHotelNameFilter,
    getMealPlanFilter,
    getStayTypeFilter,
    getHomeRoomsAndBedsFilter,
    getHomeAmenitiesFilter,
  ],
  (
    starRating,
    price,
    cancellation,
    amenities,
    hasPrice,
    payLater,
    name,
    meal,
    stayType,
    homeRoomsAndBeds,
    homeAmenities
  ) => ({
    starRating,
    price,
    cancellation,
    amenities,
    hasPrice,
    payLater,
    name,
    meal,
    stayType,
    homeRoomsAndBeds,
    homeAmenities,
  })
);

export const getCurrentAvailabilityRequest = createSelector(
  getSearch,
  getMapBoundingBox,
  (search, boundingBox) => {
    if (!search) return undefined;

    const { isDesktopAndUp } = getDeviceType();
    const platform = isDesktopAndUp ? Platform.Desktop : Platform.Mobile;

    switch (search.searchType) {
      case LodgingSelectionEnum.Location: {
        if (!boundingBox) return undefined;

        const { northEast, southWest } = boundingBox;

        return {
          LodgingSelection: search.searchType,
          request: {
            stayDates: search.dateRange,
            guests: search.guests,
            rooms: search.rooms,
            platform,
            location: {
              descriptor: {
                northEast: toHotelCoordinatesType(northEast),
                southWest: toHotelCoordinatesType(southWest),
                LocationDescriptor: LocationDescriptorEnum.BoundingBox,
              },
              LodgingSelection: LodgingSelectionEnum.Location,
            },
          },
        };
      }
      case LodgingSelectionEnum.Place: {
        return {
          LodgingSelection: search.searchType,
          request: {
            stayDates: search.dateRange,
            guests: search.guests,
            rooms: search.rooms,
            platform,
            place: search.place,
          },
        };
      }
      case LodgingSelectionEnum.LodgingIds:
      case LodgingSelectionEnum.Market: {
        return undefined;
      }
      default:
        ensureExhaustive(search.searchType, undefined);
    }
  }
);

export const getAvailabilityLodgingTypeCount = createSelector(
  getHotels,
  (lodgings) => {
    return lodgings.reduce(
      (acc, l) => {
        const isAvailable = l.price != null;
        const stayType = l.stayType ?? StayType.Hotels;
        const isPremier = l.lodgingCollection === LodgingCollection.Premier;
        const isLifestyle = l.lodgingCollection === LodgingCollection.Lifestyle;

        isAvailable ? acc.available++ : acc.unavailable++;
        if (isPremier) acc.premierCount++;
        if (isLifestyle) acc.lifestyleCount++;

        switch (stayType) {
          case StayType.Homes: {
            isAvailable ? acc.homes.available++ : acc.homes.unavailable++;
            if (isPremier) acc.homes.premierCount++;
            if (isLifestyle) acc.homes.lifestyleCount++;
            break;
          }
          case StayType.Hotels: {
            isAvailable ? acc.hotel.available++ : acc.hotel.unavailable++;
            if (isPremier) acc.hotel.premierCount++;
            if (isLifestyle) acc.hotel.lifestyleCount++;
            break;
          }
          default: {
            ensureExhaustive(stayType);
          }
        }

        return acc;
      },
      {
        hotel: {
          unavailable: 0,
          available: 0,
          premierCount: 0,
          lifestyleCount: 0,
        },
        homes: {
          unavailable: 0,
          available: 0,
          premierCount: 0,
          lifestyleCount: 0,
        },
        unavailable: 0,
        available: 0,
        premierCount: 0,
        lifestyleCount: 0,
      }
    );
  }
);
